import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "./baseQuery";

const baseQueryWithChangeRequests = (args, api, extraOptions) => {
    if (typeof args === 'string') {
        args = { url: args };
    }
    args.url = `/change-requests${args.url || ''}`;
    return baseQueryWithAuth(args, api, extraOptions);
};

export const changeRequestApi = createApi({
  reducerPath: "changeRequestApi",
  baseQuery: baseQueryWithChangeRequests,
  tagTypes: ["ChangeRequest"],
  endpoints: (builder) => ({
    // Paginated query - admin gets all, users get filtered by backend
    getChangeRequests: builder.query({
      query: ({ pageSize = 100, nextToken, page = 1 } = {}) => ({
        url: "",
        params: { pageSize, ...(nextToken ? { nextToken } : {}), page },
      }),
      providesTags: ["ChangeRequest"],
    }),
    // Get all change requests - admin gets all, users get filtered by backend
    getAllChangeRequests: builder.query({
      query: ({ searchQuery = "", createdFrom = "", createdTo = "", page = 1, limit = 100 } = {}) => ({
        url: "",
        params: {
          ...(searchQuery && { search: searchQuery }),
          ...(createdFrom && { createdFrom }),
          ...(createdTo && { createdTo }),
          page,
          limit
        },
      }),
      providesTags: ["ChangeRequest"],
      refetchOnMountOrArgChange: true,
    }),
    // Get user's specific change requests (alternative approach)
    getUserChangeRequests: builder.query({
      query: (profileId) => `?company_profile_id=${profileId}`,
      providesTags: ["ChangeRequest"],
    }),
    getChangeRequestById: builder.query({
      query: (id) => `/${id}`,
      providesTags: (result, error, id) => [{ type: "ChangeRequest", id }],
    }),
    createChangeRequest: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["ChangeRequest"],
    }),
    updateChangeRequest: builder.mutation({
      query: ({ id, ...data }) => {
        const body = Object.prototype.hasOwnProperty.call(data, 'body') ? data.body : data;
        return ({
          url: `/${id}`,
          method: "PUT",
          body,
        });
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "ChangeRequest", id },
        "ChangeRequest",
      ],
    }),
    deleteChangeRequest: builder.mutation({
      query: (id) => ({
        url: `/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChangeRequest"],
    }),
    getAvailableTransitions: builder.query({
      query: (id) => `/${id}/transitions`,
      providesTags: (result, error, id) => [{ type: "ChangeRequest", id }],
    }),
    triggerStatusTransition: builder.mutation({
      query: ({ id, transitionId }) => ({
        url: `/${id}/transition/${transitionId}`,
        method: "POST",
        body: {},
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ChangeRequest", id },
        "ChangeRequest",
      ],
    }),
  }),
});

export const {
  useGetChangeRequestsQuery,
  useGetAllChangeRequestsQuery,
  useLazyGetAllChangeRequestsQuery,
  useGetUserChangeRequestsQuery,
  useGetChangeRequestByIdQuery,
  useCreateChangeRequestMutation,
  useUpdateChangeRequestMutation,
  useDeleteChangeRequestMutation,
  useGetAvailableTransitionsQuery,
  useTriggerStatusTransitionMutation,
} = changeRequestApi;
